import React, { useState, useEffect } from "react";
import Avatar from "../ui/Avatar";
import useEmployeeRequestsSocket from "../../hooks/useEmployeeRequestsSocket";
import employeeRequestService from "../../api/employeeRequestService";
import { REQUESTS_CHANGED_EVENT } from "../employee-requests/constants";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  LayoutDashboard,
  Users,
  Calendar,
  FileText,
  ClipboardList,
  Ticket,
  MessageSquare,
  Sparkles,
  Beaker,
  Home,
  Briefcase,
  MessageCircle,
  Layers,
  UserCheck,
  Activity,
  MonitorPlay,
  DollarSign,
  StickyNote,
  BookOpen,
  Clock,
  LayoutGrid,
  Inbox,
  FolderKanban,
  CalendarRange,
  LogOut,
  Radio,
} from "lucide-react";
import SidebarSection from "./SidebarSection";
import ABSBanner from "../../assets/abs-banner.gif";
import ABSLogoVector from "../../assets/ABS-Logo/Vector/Vector.png";
import useAuth from "../../hooks/useAuth";
import useLogout from "../../hooks/useLogout";
import { useLivePresence } from "../presence/usePresence";
import usePresenceAccess from "../presence/usePresenceAccess";
import { isAdmin } from "../../utils/permissions";

export const SIDEBAR_WIDTH = 252;
export const SIDEBAR_COLLAPSED_WIDTH = 76;

const Sidebar = ({
  isMobile = false,
  mobileMenuOpen = false,
  onCloseMobileMenu,
  isImmersive = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { auth } = useAuth();
  const logout = useLogout();
  const employee = auth?.employee;
  const isAdminUser = isAdmin(employee);
  // Team Presence: admins and squad leads (leads see their squads only).
  const { access: presenceAccess } = usePresenceAccess(!isAdminUser);
  const canSeePresence = isAdminUser || Boolean(presenceAccess?.isLead);
  // People online on Discord right now (polled every 60s).
  const { data: liveData } = useLivePresence(canSeePresence, "team");
  const onlineNow = liveData?.online || 0;
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved === "true";
  });
  // Emit event when collapse state changes and save to localStorage
  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", isCollapsed.toString());
    window.dispatchEvent(
      new CustomEvent("sidebarCollapse", { detail: { isCollapsed } }),
    );
  }, [isCollapsed]);

  // Listen for toggle events from navbar
  useEffect(() => {
    const handleToggle = () => {
      setIsCollapsed((prev) => !prev);
    };

    window.addEventListener("toggleSidebar", handleToggle);
    return () => window.removeEventListener("toggleSidebar", handleToggle);
  }, []);

  // Unified task board for everyone (visibility is enforced per squad by the API).
  const [requestsUnread, setRequestsUnread] = useState(0);
  useEmployeeRequestsSocket({ token: auth?.accessToken, enabled: true });
  useEffect(() => {
    if (!isAdminUser) return undefined;
    const load = () =>
      employeeRequestService.unreadCount().then(setRequestsUnread).catch(() => {});
    load();
    // The socket pushes changes; this slow poll is only a safety net.
    const timer = setInterval(load, 5 * 60000);
    window.addEventListener(REQUESTS_CHANGED_EVENT, load);
    return () => {
      clearInterval(timer);
      window.removeEventListener(REQUESTS_CHANGED_EVENT, load);
    };
  }, [isAdminUser]);

  const boardItems = [
    { path: "/task-board", icon: LayoutGrid, label: "Task Board" },
    { path: "/task-board/projects", icon: FolderKanban, label: "Projects", matchPathPrefix: true },
    { path: "/task-board/squads", icon: Users, label: "Squads", matchPathPrefix: true },
    { path: "/task-board/sprints", icon: CalendarRange, label: "Sprints", matchPathPrefix: true },
    // Visible to everyone (view-only for non-admins); only admins have an unread count.
    { path: "/task-board/employee-requests", icon: Inbox, label: "Employee Requests", count: requestsUnread },
  ];

  const navigationSections = [
    {
      title: "Main",
      icon: Home,
      items: [
        { path: "/main/dashboard", icon: LayoutDashboard, label: "Dashboard" },
        { path: "/main/week-schedule", icon: Calendar, label: "Week Schedule" },
        {
          path: "/main/assigned-to-you",
          icon: UserCheck,
          label: "Assigned to You",
        },
        { path: "/main/documentation", icon: BookOpen, label: "Documentation" },
      ],
    },
    {
      title: "Task Management",
      icon: Layers,
      items: boardItems,
    },
    {
      title: "AI Studio",
      icon: Sparkles,
      items: [
        { path: "/ai-studio", icon: MessageSquare, label: "Workspace" },
        { path: "/ai-studio/qa-engineer", icon: Beaker, label: "QA Engineer" },
      ],
    },
    {
      title: "Management",
      icon: Briefcase,
      items: [
        ...(isAdminUser
          ? [
              {
                path: "/management/employees",
                icon: Users,
                label: "Employees",
              },
              { path: "/management/hr-form", icon: FileText, label: "HR Form" },
            ]
          : []),
        {
          path: "/management/requests",
          icon: ClipboardList,
          label: "Requests",
        },
        {
          path: "/management/attendance",
          icon: Clock,
          label: "Attendance",
        },
        ...(canSeePresence
          ? [{ path: "/team-presence", icon: Radio, label: "Team Presence", count: onlineNow }]
          : []),
        ...(isAdminUser
          ? [
              {
                path: "/management/attendance/admin",
                icon: Clock,
                label: "All Attendance",
              },
            ]
          : []),
        {
          path: "/management/monthly-report",
          icon: FileText,
          label: "Monthly Report",
        },
        ...(isAdminUser
          ? [
              {
                // matchPathPrefix so the item stays highlighted on the settings
                // and session-detail sub-routes.
                path: "/management/monitoring",
                icon: MonitorPlay,
                label: "Monitoring",
                matchPathPrefix: true,
              },
              {
                path: "/management/system-status",
                icon: Activity,
                label: "System Status",
              },
            ]
          : []),
      ],
    },
    {
      title: "Communication",
      icon: MessageCircle,
      items: [
        {
          path: "/communication/tickets",
          icon: Ticket,
          label: "Tickets",
          matchPathPrefix: true,
        },
        { path: "/communication/chat", icon: MessageSquare, label: "Chat" },
        {
          path: "/communication/meeting-notes",
          icon: StickyNote,
          label: "Notes",
          matchPathPrefix: true,
        },
        {
          path: "/communication/expenses",
          icon: DollarSign,
          label: "Expenses",
        },
      ],
    },
  ];

  const roleLabel = isAdminUser ? "Admin" : "Member";

  const handleNavigate = (path) => {
    if (isMobile && onCloseMobileMenu) onCloseMobileMenu();
    navigate(path);
  };

  // Mobile: overlay backdrop when drawer is open
  const showOverlay = isMobile && mobileMenuOpen;
  const width = isMobile
    ? SIDEBAR_WIDTH
    : isCollapsed
      ? SIDEBAR_COLLAPSED_WIDTH
      : SIDEBAR_WIDTH;
  const translateX = isImmersive
    ? -(SIDEBAR_WIDTH + 20) // fully off-screen left
    : isMobile && !mobileMenuOpen
      ? -SIDEBAR_WIDTH - 20
      : 0;

  return (
    <>
      {/* Backdrop on mobile when drawer is open */}
      {showOverlay && (
        <div
          role="button"
          tabIndex={0}
          aria-label="Close menu"
          onClick={onCloseMobileMenu}
          onKeyDown={(e) => e.key === "Escape" && onCloseMobileMenu()}
          className="fixed inset-0 bg-overlay backdrop-blur-sm z-[48] md:hidden"
        />
      )}

      <motion.aside
        initial={false}
        animate={{
          x: translateX,
          width,
        }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        className={`fixed left-0 top-0 h-screen flex flex-col z-50 overflow-visible bg-side border-r border-line backdrop-blur-[24px] backdrop-saturate-[1.4]${isImmersive ? "" : " md:!translate-x-0"}`}
        style={{ maxWidth: isMobile ? "min(280px, 85vw)" : undefined }}
      >
        {/* Brand: animated banner (filter darkens it in dark mode) / glowing mark on the rail */}
        <div className={`shrink-0 ${isCollapsed && !isMobile ? "flex justify-center pt-5 pb-4" : "p-3"}`}>
          {isCollapsed && !isMobile ? (
            <img src={ABSLogoVector} alt="ABS" className="h-7 w-auto object-contain animate-logo-glow" />
          ) : (
            <div className="h-[120px] rounded-2xl border border-line overflow-hidden" style={{ background: "var(--banner-bg)" }}>
              <img
                src={ABSBanner}
                alt="ABS"
                className="w-full h-full object-cover"
                style={{ filter: "var(--banner-filter)" }}
              />
            </div>
          )}
        </div>

        {/* Navigation: on mobile always show expanded */}
        <nav className={`sidebar-scroll flex-1 overflow-y-auto overflow-x-visible pb-3 ${isCollapsed && !isMobile ? "px-3" : "px-3"}`}>
          {navigationSections.map((section) => (
            <SidebarSection
              key={section.title}
              title={section.title}
              icon={section.icon}
              items={section.items}
              currentPath={location.pathname}
              itemMatchesPath={(item, path) =>
                item.matchPathPrefix
                  ? path === item.path || path.startsWith(item.path + "/")
                  : path === item.path
              }
              onNavigate={handleNavigate}
              isCollapsed={isMobile ? false : isCollapsed}
            />
          ))}
        </nav>

        {/* User card: opens the profile; logout beside the name */}
        <div className="shrink-0 p-3">
          <div
            className={`group flex items-center gap-2 rounded-2xl border border-line bg-panel backdrop-blur-xl transition-colors hover:border-line2 ${
              isCollapsed && !isMobile ? "flex-col p-1.5" : "p-2"
            } ${location.pathname === "/profile" ? "border-accent-line" : ""}`}
          >
            <button
              type="button"
              onClick={() => handleNavigate("/profile")}
              title="My profile"
              aria-label="Open my profile"
              className="flex items-center gap-2.5 min-w-0 flex-1 rounded-xl text-left"
            >
              <Avatar src={employee?.photo} name={employee?.fullNameEn} shape="rounded" preview={false} className="!w-[34px] !h-[34px] !text-[13px]" />
              {!(isCollapsed && !isMobile) && (
                <span className="min-w-0">
                  <span className="block text-[13.5px] font-medium text-ink truncate">{employee?.fullNameEn || "—"}</span>
                  <span className="block text-[11.5px] text-faint truncate">
                    {roleLabel} · v{process.env.REACT_APP_VERSION || "1.0.0"}
                  </span>
                </span>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                logout();
                navigate("/login", { replace: true });
              }}
              title="Log out"
              aria-label="Log out"
              className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-faint hover:text-danger hover:bg-danger-soft transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </motion.aside>
    </>
  );
};

export default Sidebar;
